import { AppContext } from '../../context';
import { BrandString, Organization, User, etchBrandTheme } from '../../types';

import React, { useContext, useEffect, useState } from 'react';

import './styles.scss';
import { convertDraftToHTML, convertMarkdownToDraft } from '@goodtimeio/rain';
import { getCandidatePortalApi } from '../../api';
import { useParams } from 'react-router-dom';
import ErrorCard from '../../components/ErrorCard';
import FooterCard from './components/FooterCard';
import HeaderCard from './components/HeaderCard';
import InterviewPlanSection from './components/InterviewPlanSection';
import ItineraryCard from './components/ItineraryCard';
import ListLanguages from '../../components/ListLanguages';
import Loading from '../../components/Loading';
import MeetInterviewerSection from './components/MeetInterviewSection';
import PrepAreaAttachments from './components/PrepAreaAttachments';
import QuestionSection from './components/QuestionSection';

interface socialLink {
    linkedin: string;
}

export interface UserWithSocialLink extends User {
    socialLinks: socialLink;
}

export interface Interview {
    id: string;
    hash: string;
    url: string;
    canReschedule: boolean;
    organizationId: string;
    coordinator: User;
    events: Event[];
    startTime: string;
    endTime: number;
    organization: Organization;
    variableResolution: VariableResolution;
    settings: {
        intent: string;
    };
}

export interface PrepAreaString {
    locale: string;
    text: string;
    default: boolean;
}

export interface PrepAreaVideo {
    locale: string;
    title: string;
    link: string;
}

export interface Event {
    title: string;
    topic: string;
    status: string;
    endTime: string;
    eventId: string;
    selectedContacts: UserWithSocialLink[];
    location: string;
    startTime: string;
    description: string;
    durationMin: number;
    desiredOrder: number;
    link: string;
    interviewId: string;
    variableResolution: VariableResolution;
}

type VariableResolution = {
    zoom: { link: string };
};
export type etchBrandThemeWithAboutCompany = etchBrandTheme & { meetAboutCompany: BrandString[] };

export interface JobStage {
    id: string;
    name: string;
    index: number;
    status: string;
}
export interface Attachment {
    fileName: string;
    locale: string;
    publicUrl: string;
    size: number;
    file: {
        name: string;
        size: number;
    };
}
export interface JobData {
    atsJobName: string;
    orgName: string;
    profileUrl: string | null;
    email: string;
    canReschedule: boolean;
    interviews: Interview[];
    allContacts: UserWithSocialLink[];
    brandTheme: etchBrandThemeWithAboutCompany;
    coordinator?: User;
    jobStages: JobStage[];
    guest?: {
        fullName: string;
        givenName: string;
        familyName: string;
        profileUrl: string;
        email: string;
    };
    interviewPrepArea?: {
        name: string;
        nameList?: PrepAreaString[];
        bodyList?: PrepAreaString[];
        youtubeVideos?: PrepAreaVideo[];
        attachments?: Attachment[];
    };
    candidatePortalSettings?: CandidatePortalSettings;
}

interface CandidatePortalSettings {
    sharePortalAccess?: boolean;
    showInterviewPlan?: boolean;
    showCoordinatorArea?: boolean;
    showMeetYourInterviewers?: boolean;
    addLinkedinUrls?: boolean;
}

const currentJobStage = (jobStages: JobStage[]) => {
    return jobStages.find((item) => item.status === 'current')?.name;
};

const changeToBrandColors = (brandTheme: etchBrandThemeWithAboutCompany | undefined) => {
    if (!brandTheme) return;
    if (brandTheme.meetCompanyColor) {
        document.documentElement.style.setProperty('--primary-date-picker-color', brandTheme?.meetCompanyColor);
    }
    if (brandTheme.meetFontColor) {
        document.documentElement.style.setProperty('--primary-text-color', brandTheme?.meetFontColor);
    }
};

const getFirstBackgroundImage = (brandTheme: etchBrandThemeWithAboutCompany | undefined) => {
    if (!brandTheme) return;
    if (brandTheme.meetBackgroundUrl) return brandTheme.meetBackgroundUrl;
    if (brandTheme.meetBackgroundUrlList?.length > 0) {
        return brandTheme.meetBackgroundUrlList[0];
    }
    return '';
};

const getBodyFromMarkdown = (text?: string | null, isMarkdown: boolean = false) => {
    if (!text) return '';

    let messageHtml = text.replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '');
    if (isMarkdown) {
        messageHtml = convertDraftToHTML(convertMarkdownToDraft(text)).replace(/<a href/g, '<a target="_blank" href');
    }

    return (
        <div
            dangerouslySetInnerHTML={{
                __html: `<span>${messageHtml}</span>`,
            }}
        />
    );
};

const CandidatePortal: React.FC = () => {
    const { translate, selectedLanguage } = useContext(AppContext);
    const [loading, setLoading] = useState(true);
    const { guestId, jobId } = useParams<{ guestId: string; jobId: string }>();
    const [portal, setPortal] = useState<JobData>();
    const [candidateHasError, setCandidateHasError] = useState<boolean>(false);
    const [arrOfEmailsOfImageError, setArrOfEmailsOfImageError] = useState<string[]>([]);
    const logo = portal?.brandTheme?.logoUrl ?? '';
    const isEmpty =
        !portal?.interviews?.length &&
        !portal?.jobStages?.length &&
        !portal?.allContacts?.length &&
        !Object.keys(portal?.brandTheme ?? {}).length &&
        !Object.keys(portal?.coordinator ?? {}).length &&
        !portal?.interviewPrepArea;

    const findPrepAreaName = (locale: string) => {
        return portal?.interviewPrepArea?.nameList?.find((item) => item.locale === locale)?.text ?? '';
    };

    const findPrepAreaBody = (locale: string) => {
        return portal?.interviewPrepArea?.bodyList?.find((item) => item.locale === locale)?.text ?? '';
    };

    const hideRightSide =
        portal?.candidatePortalSettings?.showMeetYourInterviewers === false &&
        portal?.candidatePortalSettings?.showCoordinatorArea === false &&
        portal?.candidatePortalSettings?.showInterviewPlan === false;

    useEffect(() => {
        if (portal?.allContacts?.length) {
            portal.allContacts.forEach((contact) => {
                const img = new Image();

                if (contact.profileUrl) {
                    img.onerror = () => {
                        setArrOfEmailsOfImageError((prev) => [...prev, contact.email]);
                    };
                    img.src = contact.profileUrl;
                }
                return () => {
                    img.onerror = null;
                };
            });
        }
    }, [portal]);

    useEffect(() => {
        async function fetchData() {
            const { hasError, ...result } = await getCandidatePortalApi(guestId, jobId);
            setPortal(result);
            changeToBrandColors(result.brandTheme);
            setLoading(false);
            setCandidateHasError(hasError);
        }
        fetchData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    if (loading) {
        return (
            <Loading
                backgroundImage={getFirstBackgroundImage(portal?.brandTheme)}
                message="Building Your Interview Portal"
            />
        );
    }

    if (candidateHasError || isEmpty) {
        return (
            <div
                className="flexContainer"
                style={{ backgroundImage: `url(${getFirstBackgroundImage(portal?.brandTheme)})` }}
            >
                <ErrorCard
                    cardTextMessage="This link has expired or is invalid.  Please contact your recruiter for more information."
                    isError
                />
            </div>
        );
    }

    return (
        <div
            className="flexContainer"
            style={{ backgroundImage: `url(${getFirstBackgroundImage(portal?.brandTheme)})` }}
        >
            <header>
                {logo && <img className="flexContainerLogo" src={logo} alt="Logo" />}
                <div className="languagesContainer">
                    <ListLanguages />
                </div>
            </header>
            <div className="bigFlexCard">
                <HeaderCard guest={portal?.guest} forText={portal?.atsJobName ?? ''} />
                <div className="bigFlexContent">
                    <div className="leftSide">
                        <h4 className="paragraph-2">{currentJobStage(portal?.jobStages ?? [])}</h4>
                        <h2 className="heading-1">{translate('candidatePortal:upcomingInterviews')}</h2>
                        <ItineraryCard
                            interviews={portal?.interviews ?? []}
                            arrOfEmailsOfImageError={arrOfEmailsOfImageError}
                        />
                        {portal?.interviewPrepArea &&
                            findPrepAreaName(selectedLanguage) &&
                            findPrepAreaBody(selectedLanguage) && (
                                <>
                                    <hr />
                                    <h2 className="heading-1">{findPrepAreaName(selectedLanguage)}</h2>
                                    {getBodyFromMarkdown(findPrepAreaBody(selectedLanguage), true)}
                                    <PrepAreaAttachments
                                        videos={portal?.interviewPrepArea?.youtubeVideos ?? []}
                                        attachments={portal?.interviewPrepArea?.attachments ?? []}
                                        selectedLanguage={selectedLanguage}
                                    />
                                </>
                            )}
                        <hr />
                        <FooterCard
                            title={`${translate('general:about', {
                                company: portal?.orgName ?? 'Company',
                            })}`}
                            socialTitle={translate('candidatePortal:followSocialMedia')}
                            brandTheme={portal?.brandTheme}
                        />
                    </div>
                    {!hideRightSide && (
                        <div className="rightSide">
                            {portal?.candidatePortalSettings?.showInterviewPlan !== false &&
                                portal?.jobStages &&
                                portal?.jobStages?.length > 0 && (
                                    <InterviewPlanSection
                                        title={`${translate('candidatePortal:interviewPlan')}`}
                                        jobStages={portal?.jobStages ?? []}
                                    />
                                )}
                            {portal?.candidatePortalSettings?.showCoordinatorArea !== false && portal?.coordinator && (
                                <>
                                    {portal?.candidatePortalSettings?.showInterviewPlan !== false && <hr />}
                                    <QuestionSection
                                        title={`${translate('candidatePortal:reachOut')}`}
                                        coordinator={
                                            portal?.coordinator ??
                                            ({
                                                email: '',
                                                fullName: '',
                                                givenName: '',
                                                familyName: '',
                                                title: '',
                                            } as User)
                                        }
                                        brandTheme={portal?.brandTheme}
                                    />
                                </>
                            )}
                            {portal?.candidatePortalSettings?.showMeetYourInterviewers !== false &&
                            portal?.allContacts?.length ? (
                                <>
                                    {(portal?.candidatePortalSettings?.showInterviewPlan !== false ||
                                        portal?.candidatePortalSettings?.showCoordinatorArea !== false) && <hr />}
                                    <MeetInterviewerSection
                                        title={`${translate('candidatePortal:meetInterviewers')}`}
                                        contacts={portal?.allContacts ?? []}
                                        arrOfEmailsOfImageError={arrOfEmailsOfImageError}
                                        showLinkedin={portal?.candidatePortalSettings?.addLinkedinUrls !== false}
                                    />
                                </>
                            ) : null}
                        </div>
                    )}
                </div>
            </div>
            <div className="AppPrivacyPolicy">
                <a
                    href="https://www.goodtime.io/privacy-policy"
                    className="policyLink"
                    target="_blank"
                    rel="noopener noreferrer"
                >
                    Privacy Policy
                </a>
            </div>
        </div>
    );
};

export default CandidatePortal;
